import { DataGrid, RowItem } from "../data-grid/data-grid";
import { customElement, BindingEngine, bindable, Container, Disposable, autoinject, transient, computedFrom } from "aurelia-framework";
import { QueryFilter, DataGridColumn } from "../../../models/queries/queries.models";
import * as Enumerable from "linq";
import { InitFilterValueConverter } from "../converters/queries/init-filter";
import { NotificationClientService } from "../../../services/basics/notification-client.service";
import { BindingSignaler } from "aurelia-templating-resources";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { I18N } from "aurelia-i18n";
import { QueryFiltersExecutor } from "../converters/queries/query-filter";
import { EntityValidationsService } from "../../../client-validation-system/validations-service";
import { DataGridEvents } from "../data-grid/data-grid-events";
import { CacheLocation, CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { Router, RouterEvent } from "aurelia-router";
import { EventAggregator } from "aurelia-event-aggregator";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { BrowserHelper } from "../../../helpers/browser-helper";
import { Reflection } from "../../../utilities/reflection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

export type RightshipDataGridColumn = DataGridColumn & {
    /** Show ellipsis after the provided amount of characters. */
    ellipsis?: number,
}

@autoinject()
@transient()
@customElement('rightship-data-grid')
export class RightshipDataGrid extends DataGrid {

    private _subscriptions: Disposable[] = [];

    protected cacheClient: CacheClientService;
    protected router: Router;
    protected eventAggregator: EventAggregator;

    public currentContext: RightshipDataGrid = this;
    public isSafari: boolean = BrowserHelper.isSafari();

    protected baseUrl: string = ConfigService.instance().config.urls.rightShipPlatformApiUrl;

    @bindable
    public theme: string = "primary";

    @bindable()
    public enableSelect: boolean = false;

    @bindable()
    public fallbackMessage: string = "No items to display";

    public constructor(
        protected initFilterConverter: InitFilterValueConverter,
        protected rightshipDataGridEl: Element,
        notificationService: NotificationClientService,
        bindingEngine: BindingEngine,
        bindingSignaler: BindingSignaler,
        queriesClientService: QueriesClientService,
        lookupService: LookupClientService,
        entityValidationsService: EntityValidationsService,
        i18N: I18N, protected userClientService: UserClientService,
        userClaimsService: UserClaimsService,
        requestClient: RequestClientService
    ) {
        super(initFilterConverter, rightshipDataGridEl, notificationService, bindingEngine, bindingSignaler,
            requestClient, queriesClientService, lookupService, userClientService, userClaimsService, i18N, entityValidationsService);
        this.cacheClient = Container.instance.get(CacheClientService);
        this.router = Container.instance.get(Router);
        this.eventAggregator = Container.instance.get(EventAggregator);
    }

    attached() {
        if (!this.pageCount) {
            this.cacheClient.get('data-grid-settings-' + this.router.currentInstruction.config.name, CacheLocation.LocalStorage, false).then((x: any) => {
                if (x) {

                    if (x.identifier) {
                        this.identifier = x.identifier;
                    }

                    if (x.pageCount) {
                        if (x.pageCount == "All") {
                            x.pageCount = 10;
                        }
                        this.pageCount = x.pageCount;
                    }
                }

                setTimeout(() => {
                    super.attached();
                });
            });
        } else {
            super.attached();
        }

        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }

        this._subscriptions.push(this.eventAggregator.subscribe(RouterEvent.Processing, (x) => {

            this.cacheClient.put('data-grid-settings-' + x.instruction.previousInstruction.config.name, {
                pageCount: this.pageCount,
                identifier: this.identifier
            }, CacheLocation.LocalStorage);
        }));
    }

    @computedFrom(Reflection.getPropertyPath<DataGrid>(x => x.itemsCollection))
    public get emptyCollection(): boolean {
        return !this.itemsCollection || this.itemsCollection.length < 1
    }

    public sortingTriggered(column: DataGridColumn): void {
        if (!this.sortingEnabled || column.disableSorting) {
            return;
        }

        if (this.isSingleColumnSorting) {
            this.itemSource.columns.forEach(x => {
                if (x !== column) {
                    x.isAscending = null;
                }
            });
        } else {
            let order = 1;
            column.isSortable = true;
            Enumerable.from(this.itemSource.columns).orderBy(x => x.sortOrder).toArray().forEach(x => {
                if (x !== column) {
                    x.sortOrder = order;
                    order++;
                } else {
                    x.sortOrder = 0;
                }
            });
        }

        if (column.isAscending == null) {
            column.isSortable = true;
            column.isAscending = column.isDescendingOnFirstSortingTriggered !== null ? !column.isDescendingOnFirstSortingTriggered : true;
        }
        else if (!column.isAscending) {
            column.isSortable = true;
            column.isAscending = column.isDescendingOnFirstSortingTriggered === true ? true : null;
        }
        else {
            column.isSortable = true;
            column.isAscending = column.isDescendingOnFirstSortingTriggered === true ? null : false;
        }

        this.reloadData("sorting");
    }

    public selectAll() {
        super.selectAll();
        this.getRowsCollection().forEach(x => {
            if ((<any>x).isSelectable != false)
                x.isSelected = true;
        });
    }

    public selectRowInMultipleSelection(rowItem: RowItem): void {
        this.handleMultipleSelection(rowItem);
    }

    public raiseItemSelectedEvent(rowItem: RowItem, fromCheckbox: boolean) {
        this.rightshipDataGridEl.dispatchEvent(
            new CustomEvent(DataGridEvents.itemSelected,
                {
                    bubbles: true,
                    detail: { item: rowItem ? rowItem.item : null, row: rowItem, identifier: this.identifier, fromCheckbox: fromCheckbox }
                })
        );
    }

    public buildODataUrl(baseUrl: string, noFilters: boolean = false, noPaging: boolean = false): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            let filtersToInclude: QueryFilter[] = [];
            if (!noFilters && this.itemSource.columns) {
                this.itemSource.columns.filter(x => x.isFilterable).forEach(x => {
                    if (x.filtersResolver) {
                        const filters = x.filtersResolver(x.filters);
                        if (filters) {
                            filtersToInclude = filtersToInclude.concat(filters);
                        }
                    } else {
                        if (x.filters) {
                            filtersToInclude = filtersToInclude.concat(x.filters);
                        }
                    }
                });
            }

            this.ensureValues();
            const pageCount = (this.pageCountSet
                && this.pageCountSet.length > 0
                && Enumerable.from(this.pageCountSet)
                    .any(x => x.label === this.pageCount)
                ? Enumerable.from(this.pageCountSet).firstOrDefault(x => x.label === this.pageCount).value
                : Number(this.pageCount));
            const limit = this.itemSource.limit && pageCount && this.itemSource.limit < pageCount ? this.itemSource.limit : pageCount;

            const included: {
                [name: string]: QueryFilter[],
            } = {};

            // Remove filters residing both in initialFilters and filtersToInclude.  Gives precedence to filtersToInclude.
            filtersToInclude.filter(x => x.filterProperty).forEach(x => {
                if (!x.filterProperty.name) {
                    return;
                }

                (included[x.filterProperty.name] || (included[x.filterProperty.name] = [])).push(x);
            });

            const initialFilters = this.initialFilters.filter(x => x.filterProperty && ((x.filterValue != null && x.filterValue != undefined) || !x.skipOnNullOrEmpty)
                && !(Array.isArray(x.filterValue) && x.filterValue.length == 0) // remove filters with empty array values
                && !included[x.filterProperty.name]);

            let sortingColumns = Enumerable.from(this.itemSource.columns).where(x => x.isSortable || x.isGrouping).toArray();
            if (sortingColumns.filter(x => (x.isAscending != null || x.isGrouping)).length == 0) {
                sortingColumns = this.itemSource.initialSorting ? this.itemSource.initialSorting : [];
            }

            const converterInitialFilters = new QueryFiltersExecutor(this.initFilterConverter,
                initialFilters,
                this.page,
                limit,
                sortingColumns, 
                this.userClientService.currentUser);
            const urlPromise = initialFilters.length > 0 ? converterInitialFilters.getUri(true, false, baseUrl) : new Promise<string>((resolve) => resolve(baseUrl));
            
            urlPromise.then(url => {
                const converter = new QueryFiltersExecutor(this.initFilterConverter,
                    filtersToInclude,
                    this.page, limit,
                    sortingColumns, 
                    this.userClientService.currentUser);
            
                converter.getUri(noPaging, true, url, limit).then(uri => {
                    resolve(uri)
                }).catch(e => {
                    console.error(e);
                    reject(e);
                });
            })
            .catch(() => 
            {
                const converter = new QueryFiltersExecutor(this.initFilterConverter,
                    filtersToInclude,
                    this.page, limit,
                    sortingColumns, 
                    this.userClientService.currentUser);
            
                return converter.getUri(noPaging, true, baseUrl, limit).then(uri => {
                    resolve(uri)
                }).catch(e => {
                    console.error(e);
                    reject(e);
                });
            });
        });
    }

    public onTooltipClick($event: Event) {
        $event.stopPropagation();
    }

    public pageChanged(newValue: any, oldValue: any): void {
        super.pageChanged(newValue, oldValue);

        if (this.rightshipDataGridEl) {
            this.rightshipDataGridEl.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }

    public async reloadData(eventSource?: string, onCompleted: () => void = null): Promise<void> {

        if (eventSource == "filter-value-change") return;

        await super.reloadData(eventSource, onCompleted);
    }

    public handleRowItem(item: RowItem, column: RightshipDataGridColumn): RowItem {
        if (!item || !item.item) return item;

        const value = item.item[column.name];

        switch (typeof (value)) {
            case 'string':
                if (!column.ellipsis || column.ellipsis === 0) {
                    break;
                }

                item.item[column.name] = value.length > column.ellipsis
                    ? value.substr(0, column.ellipsis) + '...'
                    : value;

                break;
            default:
                break;
        }

        return item;
    }

    public selectRow(item: RowItem) {
        if (!this.enableSelect) {
            return true;
        }

        super.selectRow(item);
        return true;
    }

    public detached() {
        super.detached();
        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }
    }
}
